/*
  トップのヒーローの自動送り

  sections/custom-hero.liquid のスライドをフェードで入れ替え、
  カウンタとプログレスバーを現在位置に合わせる。

  実装仕様
  #1 スライドは重ねて置いてあり、出ている1枚にだけ data active を付ける。
     見た目の切り替えはCSSのフェードが担当し、JSは順番だけを管理する
  #2 止める条件は4つ。指やマウスで押している / キーボードフォーカスが中にある /
     タブが裏に回った / セクションが画面外。どれかが解けたら再開する。
     マウスが乗っているだけでは止めない（2026年9月29日 PM指摘）。ファーストビューは
     画面の大半を占めるので、PCではカーソルがほぼ常に上にあり、送りも枚数の動きも見えなかった
  #3 prefers reduced motion のときは自動送りをしない
  #4 横に動かす作りではないので、スワイプはここで拾って次の1枚に読み替える。
     縦に振れているときは無視する（ページのスクロールを邪魔しないため）
  #5 shopify:section:load で作り直す。テーマエディタで設定を変えたあとも動くように
  #6 JSが読めなくても1枚目には最初から印が付いているので、静止画として出る
  #8 今の枚数の数字を縦に入れ替える（2026年9月29日 PM指定）。
     前の数字に data leaving を付けて下へ消し、次の数字に data active を付けて
     上から降ろす。動きはCSSが持つ。次の数字が直前に下へ抜けたものだと
     下から上がってきてしまうので、印を外して上の控えに瞬時に戻してから降ろす
  #7 見出しを1文字ずつ出す（2026年9月23日 PM指定）。
     ここがやるのは分解と通し番号だけで、動きはCSSが持つ（#1と同じ分担）。
     文字の span はJSが作るので、JSが動かなければ span が存在せず、
     CSSの伏せる指定も当たらない。素の見出しが最初から読める
*/
(function () {
  'use strict';

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SWIPE_MIN = 40;
  var instances = [];

  /*
    見出しを1文字ずつに分ける。実装仕様 #7

    文字は素の inline のまま置く。inline なら折り返しの規則が変わらないので、
    単語の途中で改行されることが無い（inline block にすると起きる）。

    読み上げは見出しごと aria label に持たせ、分けた中身は隠す。
    でないと1文字ずつ読み上げられる。

    ずらしは1文字80msを上限に、全体が1200msを超えないよう詰める
    （2026年9月29日 PM指摘で40ms・600msから倍にした。1秒ほどで出終わり、気づけなかった）。
    上限の1200msは1文字の出現の長さ（--motion-duration）と同じで、最後の文字が
    出始めるころに最初の文字が出終わる。見出しはセクション設定なので言語で文字数が変わる
    （独語は英語比で3割前後長い）
  */
  var CHAR_STAGGER = 80;
  var CHAR_TOTAL = 1200;

  function splitTitle(hero) {
    var title = hero.querySelector('[data-c-hero-title]');

    if (!title || title.dataset.cHeroSplit === 'true') {
      return;
    }

    /* 動きを望まない人には分解もしない。素の文字のまま出す */
    if (motionQuery.matches) {
      return;
    }

    var text = title.textContent.replace(/\s+/g, ' ').trim();

    if (!text) {
      return;
    }

    var letters = text.replace(/ /g, '').length;
    var step = Math.min(CHAR_STAGGER, CHAR_TOTAL / Math.max(1, letters));
    var holder = document.createElement('span');
    var index = 0;

    holder.className = 'c-hero__chars';
    holder.setAttribute('aria-hidden', 'true');

    text.split('').forEach(function (character) {
      if (character === ' ') {
        holder.appendChild(document.createTextNode(' '));
        return;
      }

      var span = document.createElement('span');

      span.className = 'c-hero__char';
      span.style.setProperty('--c-hero-char', String(index));
      span.textContent = character;
      holder.appendChild(span);
      index += 1;
    });

    title.setAttribute('aria-label', text);
    title.style.setProperty('--c-hero-char-stagger', step + 'ms');
    title.textContent = '';
    title.appendChild(holder);
    title.dataset.cHeroSplit = 'true';
  }

  function create(hero) {
    var rail = hero.querySelector('[data-c-hero-rail]');

    if (!rail || rail.children.length < 2) {
      return null;
    }

    var slides = Array.prototype.slice.call(rail.children);
    var counter = hero.querySelector('[data-c-hero-counter]');
    var count = hero.querySelector('[data-c-hero-count]');
    var nums = count ? Array.prototype.slice.call(count.children) : [];
    var bar = hero.querySelector('[data-c-hero-bar]');
    var total = slides.length;
    var interval = parseInt(hero.dataset.interval, 10) || 6000;
    var wantsAutoplay = hero.dataset.autoplay === 'true';
    var current = 0;
    var timer = null;
    var held = false;
    var visible = true;
    var startX = 0;
    var startY = 0;

    /* 実装仕様 #8 */
    function roll(from, to) {
      var leaving = nums[from];
      var entering = nums[to];

      if (!leaving || !entering || leaving === entering) {
        return;
      }

      entering.style.transition = 'none';
      entering.removeAttribute('data-leaving');
      void entering.offsetWidth;
      entering.style.transition = '';

      leaving.removeAttribute('data-active');
      leaving.setAttribute('data-leaving', '');
      entering.setAttribute('data-active', '');
    }

    function show(next) {
      var previous = current;

      current = (next + total) % total;
      roll(previous, current);

      slides.forEach(function (slide, i) {
        if (i === current) {
          slide.setAttribute('data-active', '');
        } else {
          slide.removeAttribute('data-active');
        }
      });

      if (counter) {
        counter.textContent = current + 1 + ' / ' + total;
      }

      if (bar) {
        bar.style.setProperty('--c-hero-progress', String((current + 1) / total));
      }
    }

    function step() {
      show(current + 1);
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();

      if (!wantsAutoplay || motionQuery.matches || held || !visible || document.hidden) {
        return;
      }

      timer = window.setInterval(step, interval);
    }

    function hold() {
      held = true;
      stop();
    }

    function release() {
      held = false;
      start();
    }

    function onPointerDown(event) {
      startX = event.clientX;
      startY = event.clientY;
      hold();
    }

    function onPointerUp(event) {
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;

      /* 縦に振れているときはページのスクロールなので触らない */
      if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
        show(dx < 0 ? current + 1 : current - 1);
      }

      release();
    }

    hero.addEventListener('pointerdown', onPointerDown);
    hero.addEventListener('pointerup', onPointerUp);
    hero.addEventListener('pointercancel', release);
    /* 押したまま外へ出て離すと pointerup が届かないので、出たところで再開する */
    hero.addEventListener('pointerleave', release);
    hero.addEventListener('focusin', hold);
    hero.addEventListener('focusout', release);
    document.addEventListener('visibilitychange', start);

    var observer = null;

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          start();
        },
        { threshold: 0.2 }
      );
      observer.observe(hero);
    }

    show(0);
    start();

    return {
      root: hero,
      destroy: function () {
        stop();
        document.removeEventListener('visibilitychange', start);

        if (observer) {
          observer.disconnect();
        }
      }
    };
  }

  function setup(root) {
    var heroes = (root || document).querySelectorAll('[data-c-hero]');

    Array.prototype.forEach.call(heroes, function (hero) {
      if (hero.dataset.cHeroReady === 'true') {
        return;
      }

      hero.dataset.cHeroReady = 'true';

      /* 送りが作れない（スライド1枚）ときも見出しは出す */
      splitTitle(hero);

      var instance = create(hero);

      if (instance) {
        instances.push(instance);
      }
    });
  }

  function teardown(root) {
    instances = instances.filter(function (instance) {
      if (!root.contains(instance.root)) {
        return true;
      }

      instance.destroy();
      return false;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setup(document);
    });
  } else {
    setup(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    setup(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });
})();
